import { call, put, select } from 'redux-saga/effects';
import { apiRequest } from '../api/api.saga-services';
import { IUser, IUserCredentials, IUserCredentialsSSO, IUserWithTokens } from '../resource/user/user.types';
import * as pageService from '../page/page.initializer.saga-service';
import * as resourceService from '../resource/resource.destructor.saga-service';
import * as api from '../api/request-config/auth.api';
import * as oauthApi from '../api/request-config/oauth.api';
import * as userSettingsService from '../page/page-storage/user-settings/user-settings.saga-services';
import * as actions from './auth.actions';
import { authSelectors } from './auth.selectors';
import { storeUser } from '../resource/user/user.actions';
import moment from 'moment';
import Cookies from 'js-cookie';
import { omit } from 'lodash';
import { INewPassword, IOAuthAuthorizePayload } from './auth.types';
import * as dialogService from '../dialog/dialog.saga-services';
import { push } from 'connected-react-router';
import { addFlashMessage } from '../../utils/flashMessages.utils';
import { pusher } from '../socket/pusher/pusher.connection';
import { userSelectors } from '../resource/user/user.selectors';
import { IMeetingSimple } from '../resource/meeting-simple/meeting-simple.types';
import { meetingSimpleSelectors } from '../resource/meeting-simple/meeting-simple.selectors';
import { SOCKET_CONFIG } from '../socket/socket.configs';
import { searchUserTenants } from '../resource/user-tenants/user-tenants.saga-services';
import { ITenant } from '../resource/user-tenants/user-tenants.types';
import { searchSettings } from '../resource/settings/settings.saga-service';

export function* updateLoggedUserdata(newUser: IUserWithTokens) {
  yield call(updateUserDataInCookie, newUser);
  yield put(actions.storeAccessToken(newUser.accessToken));
  yield put(actions.storeUserIsAdmin(newUser.isAdmin === '1'));
  yield put(actions.storeLoggedUser(newUser.id));
  yield put(storeUser(omit(newUser, 'token')));
}

function* updateUserDataInCookie(newUser: IUserWithTokens) {
  const user = (yield call(getCookie, 'logged_user')) as string | undefined;
  if (!user) {
    return;
  }
  // Persist tenant with the session cookie. After a workspace switch, reload hydrates
  // from this cookie; the JWT no longer carries tenant_id, so token + isAdmin alone
  // would restore the login workspace instead of the one the user switched to.
  const newParsedUser = {
    ...JSON.parse(user),
    accessToken: newUser.accessToken,
    isAdmin: newUser.isAdmin,
    tenantId: newUser.tenantId,
    tenantName: newUser.tenantName,
  };
  yield call([Cookies, Cookies.set], 'logged_user', JSON.stringify(newParsedUser));
}

export function* logUserIn(user: IUserWithTokens) {
  // order matters!
  yield put(actions.storeLoggedUser(user.id));

  const redirectAfterLogin = (yield select(authSelectors.selectRedirectAfterLogin)) as string | null;
  const hasRedirectAfterLogin = !!redirectAfterLogin;

  yield put(actions.storeAccessToken(user.accessToken));
  yield put(storeUser(omit(user, 'token')));

  const tenants = (yield call(searchUserTenants)) as ITenant[] | undefined;
  // Tenant name is not on the JWT; attach it from the memberships list so the UI
  // can show the current workspace after login or a cached-session restore.
  const currentTenant = tenants?.find((tenant) => tenant.id === user.tenantId);
  if (currentTenant?.name) {
    yield put(storeUser({ ...omit(user, 'token'), tenantName: currentTenant.name }));
  }
  yield call(searchSettings);
  yield call(userSettingsService.getUserSettings);

  if (hasRedirectAfterLogin) {
    yield put(actions.clearRedirectAfterLogin());
    yield put(push(redirectAfterLogin));
    return;
  }

  const lastTenantSelected = localStorage.getItem('last_tenant_selected');
  if (lastTenantSelected === null && (tenants?.length ?? 0) > 1) {
    yield put(push(`/workspaces`));
  }
}

export function* cacheLoggedUser(user: IUserWithTokens, rememberMe: boolean, expiresAt: string) {
  yield call([Cookies, Cookies.set], 'logged_user', JSON.stringify({ ...user, rememberMe, expiresAt }), {
    expires: rememberMe ? 30 : undefined,
  });
}
export function getCookie(key: string) {
  return Cookies.get(key);
}
export function* loadCachedUser() {
  const user = (yield call(getCookie, 'logged_user')) as string | undefined;

  if (!user) {
    return;
  }

  try {
    const parsedUser = JSON.parse(user);
    // Cookie may still have the login tenant (older cookies, or a switch that did not
    // persist tenantId). last_tenant_selected is written on every switch and is the
    // source of truth for which workspace to restore on reload.
    const lastTenantSelected = localStorage.getItem('last_tenant_selected');
    if (lastTenantSelected && lastTenantSelected !== parsedUser.tenantId) {
      parsedUser.tenantId = lastTenantSelected;
    }
    yield put(actions.storeRememberUser(parsedUser.rememberMe));
    yield put(actions.storeUserExpiresAt(parsedUser.expiresAt));
    yield put(actions.storeUserIsAdmin(parsedUser.isAdmin === '1'));
    delete parsedUser.rememberMe;
    delete parsedUser.expiresAt;
    delete parsedUser.isAdmin;
    yield call(logUserIn, parsedUser);
  } catch (e) {
    console.error(e);
  }
}

export function* logIn(credentials?: IUserCredentials | IUserCredentialsSSO, userWithToken?: IUserWithTokens) {
  const user: IUserWithTokens | undefined =
    userWithToken ?? (credentials ? yield call(apiRequest, api.loginApi(credentials)) : undefined);
  if (!user) {
    return;
  }

  yield put(actions.storeRememberUser(!!credentials?.rememberUser));
  const timestamp = credentials?.rememberUser ? moment().add(30, 'days') : moment().add(1, 'day');
  yield put(actions.storeUserExpiresAt(timestamp.toString()));
  yield put(actions.storeUserIsAdmin(user.isAdmin === '1'));

  yield call(logUserIn, user);
  yield call(cacheLoggedUser, user, !!credentials?.rememberUser, timestamp.toString());
}

export function* logOutIfUserExpired() {
  const expiresAtString = (yield select(authSelectors.selectUserExpiresAt)) as string;

  if (moment(expiresAtString).isSameOrBefore(moment())) {
    yield call(logOut);
    return true;
  }
  return false;
}

export function* logOut() {
  yield call(unsubscribePusherChannels);
  yield put(actions.clearAccessToken());
  yield put(actions.clearLoggedUser());
  yield put(actions.clearGooglePermisionGranted());
  yield put(actions.clearRememberUser());
  yield put(actions.clearUserExpiresAt());
  yield put(actions.clearUserIsAdmin());
  yield put(actions.clearRedirectAfterLogin());

  yield call(pageService.uninitPage);
  yield call(resourceService.uninitResources);

  yield call([Cookies, Cookies.remove], 'logged_user');
  // window.removeEventListener('unload', (event) => {
  //   Cookies.remove('logged_user');
  // });
}

export function* storeGooglePermisionGranted(permision: boolean) {
  const loggedUserString = (yield call(getCookie, 'logged_user')) as string | undefined;
  if (!loggedUserString) {
    return;
  }
  const loggedUser = JSON.parse(loggedUserString);
  yield call(
    cacheLoggedUser,
    { ...loggedUser, googleApiToken: permision ? 'true' : '' },
    loggedUser.rememberMe,
    loggedUser.expiresAt,
  );
}

export function* forgotPasswordEmail(email: string) {
  const success = (yield call(apiRequest, api.forgotPasswordApi(email))) as boolean;
  if (!success) {
    return;
  }
  // console.log(success);
  yield call(dialogService.closeDialog, 'forgotPasswordDialog');
  yield call(addFlashMessage, { type: 'success', message: 'An email with reset instructions was sent to your inbox.' });
}

export function* saveNewPassword(data: INewPassword) {
  const success = (yield call(apiRequest, api.saveNewPasswordApi(data))) as { status: string } | null;
  console.debug('Save API', success);
  if (!success) {
    return;
  }

  yield put(push('/login'));
  yield call(addFlashMessage, { type: 'success', message: 'Your password was reset.' });
}

export function* oauthAuthorize(payload: IOAuthAuthorizePayload) {
  const result = (yield call(
    apiRequest,
    oauthApi.oauthAuthorizeApi({
      redirect_uri: payload.redirectUri,
      scope: payload.scope,
    }),
  )) as { code: string } | null;
  if (!result) {
    if (payload.onError) {
      payload.onError('Unable to authorize this integration request');
    }
    return;
  }

  payload.onSuccess(result.code);
}

function* unsubscribePusherChannels() {
  const loggedUserId = (yield select(authSelectors.selectLoggedUser)) as IUser['id'];
  const loggedUser = (yield select(userSelectors.selectResourceById, loggedUserId)) as IUser;
  const meetingIDs = (yield select(meetingSimpleSelectors.selectResourceIds)) as [IMeetingSimple['id']];
  let meetingPusherChannelName = '';

  try {
    meetingPusherChannelName = `${SOCKET_CONFIG.channelPrefix}-${loggedUser.tenantId}`;
    pusher.unsubscribe(meetingPusherChannelName);
  } catch (error) {
    console.warn(
      `The Pusher channel '${meetingPusherChannelName}' is already unsubscribed or does not exist. So do nothing.`,
    );
  }

  meetingIDs.forEach((meetingID: string) => {
    try {
      meetingPusherChannelName = `${SOCKET_CONFIG.channelPrefix}-${loggedUser.tenantId}-${meetingID}`;
      pusher.unsubscribe(meetingPusherChannelName);
    } catch (error) {
      console.warn(
        `The Pusher channel '${meetingPusherChannelName}' is already unsubscribed or does not exist. So do nothing.`,
      );
    }
  });
}
